import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Loader2, 
  ChevronLeft,
  ChevronRight,
  Download,
  Printer,
  Star,
  Target,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Clock,
  DollarSign,
  Users,
  Shield,
  Briefcase,
  MapPin,
  Mail,
  Calendar,
  Award,
  Zap,
  Lock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEGMENT_COLORS = {
  "Core Builder": { bg: '#1B5E20', text: 'white' },
  "Emerging Builder": { bg: '#1565C0', text: 'white' },
  "Support Developer": { bg: '#F9A825', text: '#1a1a1a' },
  "Transition Candidate": { bg: '#C62828', text: 'white' }
};

const SegmentBadge = ({ segment, size = "md" }) => {
  if (!segment) return null;
  const colors = SEGMENT_COLORS[segment] || { bg: '#94a3b8', text: 'white' };
  const sizeClasses = size === "lg" ? "px-4 py-1.5 text-sm" : "px-2 py-0.5 text-xs";
  return (
    <span 
      className={`rounded font-semibold ${sizeClasses}`}
      style={{ backgroundColor: colors.bg, color: colors.text }}
    >
      {segment.toUpperCase()}
    </span>
  );
};

const ActionBadge = ({ action, size = "md" }) => {
  if (!action) return null;
  const colors = {
    "Retain": "bg-emerald-600 text-white",
    "Develop": "bg-blue-600 text-white",
    "Reassign": "bg-amber-600 text-white",
    "Transition": "bg-red-600 text-white"
  };
  const sizeClasses = size === "lg" ? "px-4 py-1.5 text-sm" : "px-2 py-0.5 text-xs";
  return (
    <span className={`rounded font-semibold ${sizeClasses} ${colors[action] || 'bg-slate-600 text-white'}`}>
      {action.toUpperCase()}
    </span>
  );
};

const StarRating = ({ rating, max = 5 }) => {
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(max)].map((_, i) => (
        <Star 
          key={i} 
          className={`w-3 h-3 ${i < rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`}
        />
      ))}
    </div>
  );
};

const RadarChart = ({ scores, size = 200 }) => {
  if (!scores) return null;
  
  const dimensions = [
    { key: 'selfAssessment', label: 'Self' },
    { key: 'managerValidation', label: 'Manager' },
    { key: 'technicalCompetency', label: 'Technical' },
    { key: 'handsOnSimulation', label: 'Hands-On' },
    { key: 'collaboration', label: 'Collab' }
  ];
  
  const center = size / 2;
  const maxRadius = (size / 2) - 30;
  const angleStep = (2 * Math.PI) / dimensions.length;
  
  // Calculate points for actual scores
  const scorePoints = dimensions.map((dim, i) => {
    const value = scores[dim.key] || 0;
    const radius = (value / 100) * maxRadius;
    const angle = i * angleStep - Math.PI / 2;
    return {
      x: center + radius * Math.cos(angle),
      y: center + radius * Math.sin(angle)
    };
  });
  
  // Calculate points for threshold (80)
  const thresholdPoints = dimensions.map((_, i) => {
    const radius = (80 / 100) * maxRadius;
    const angle = i * angleStep - Math.PI / 2;
    return {
      x: center + radius * Math.cos(angle),
      y: center + radius * Math.sin(angle)
    };
  });
  
  const scorePath = scorePoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
  const thresholdPath = thresholdPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
  
  return (
    <svg width={size} height={size} className="mx-auto">
      {/* Grid circles */}
      {[20, 40, 60, 80, 100].map(level => (
        <circle
          key={level}
          cx={center}
          cy={center}
          r={(level / 100) * maxRadius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="1"
        />
      ))}
      
      {/* Grid lines */}
      {dimensions.map((_, i) => {
        const angle = i * angleStep - Math.PI / 2;
        return (
          <line
            key={i}
            x1={center}
            y1={center}
            x2={center + maxRadius * Math.cos(angle)}
            y2={center + maxRadius * Math.sin(angle)}
            stroke="#e5e7eb"
            strokeWidth="1"
          />
        );
      })}
      
      {/* Threshold polygon */}
      <path
        d={thresholdPath}
        fill="none"
        stroke="#94a3b8"
        strokeWidth="2"
        strokeDasharray="4 4"
      />
      
      {/* Score polygon */}
      <path
        d={scorePath}
        fill="rgba(21, 101, 192, 0.2)"
        stroke="#1565C0"
        strokeWidth="2"
      />
      
      {/* Labels */}
      {dimensions.map((dim, i) => {
        const angle = i * angleStep - Math.PI / 2;
        const labelRadius = maxRadius + 20;
        return (
          <text
            key={dim.key}
            x={center + labelRadius * Math.cos(angle)}
            y={center + labelRadius * Math.sin(angle)}
            textAnchor="middle"
            dominantBaseline="middle"
            className="text-[10px] fill-slate-600"
          >
            {dim.label}
          </text>
        );
      })}
    </svg>
  );
};

export default function EmployeeProfile() {
  const navigate = useNavigate();
  const { employeeId } = useParams();
  const [searchParams] = useSearchParams();
  const returnTab = searchParams.get('from') || 'overview';
  
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [employeeList, setEmployeeList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);

  useEffect(() => {
    const token = localStorage.getItem("executiveToken");
    if (!token) {
      navigate("/executive");
      return;
    }
    fetchProfile();
    fetchEmployeeList();
  }, [employeeId, navigate]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("executiveToken")}` }
  });

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/executive/employee/${employeeId}`, getAuthHeaders());
      setProfile(response.data);
    } catch (err) {
      console.error("Error fetching profile:", err);
      if (err.response?.status === 401) {
        localStorage.removeItem("executiveToken");
        navigate("/executive");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeList = async () => {
    try {
      const response = await axios.get(`${API}/executive/employees-list`, getAuthHeaders());
      setEmployeeList(response.data.employees);
      const idx = response.data.employees.findIndex(e => e.id === employeeId);
      setCurrentIndex(idx);
    } catch (err) {
      console.error("Error fetching employee list:", err);
    }
  };

  const navigateToEmployee = (direction) => {
    const newIndex = currentIndex + direction;
    if (newIndex >= 0 && newIndex < employeeList.length) {
      navigate(`/executive/profile/${employeeList[newIndex].id}?from=${returnTab}`);
    }
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return "N/A";
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F3F0]">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#1a3a3a] mx-auto mb-4" />
          <p className="text-muted-foreground">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F3F0]">
        <div className="text-center">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-4" />
          <p className="text-muted-foreground">Profile not found</p>
          <Button onClick={() => navigate('/executive/dashboard')} className="mt-4">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const hasAssessmentData = profile.briScore !== null;

  return (
    <div className="min-h-screen bg-[#F5F3F0]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#1a3a3a] text-white shadow-lg">
        <div className="max-w-[1400px] mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/executive/dashboard')}
                className="text-white hover:bg-white/10 gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Back to Dashboard
              </Button>
              <div className="h-6 w-px bg-white/20" />
              <span className="text-sm text-white/70">Individual Profile</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" className="text-white hover:bg-white/10 gap-2">
                <Download className="w-4 h-4" />
                Export PDF
              </Button>
              <Button variant="ghost" size="sm" className="text-white hover:bg-white/10 gap-2">
                <Printer className="w-4 h-4" />
                Print
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-6">
        {/* Profile Header Section */}
        <Card className="bg-white shadow-sm mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Left: Avatar and basic info */}
              <div className="flex items-start gap-4 flex-1">
                <div className="w-20 h-20 bg-[#1a3a3a] rounded-full flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                  {profile.fullName?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl font-bold text-[#1a3a3a]">{profile.fullName}</h1>
                  <p className="text-muted-foreground">{profile.jobTitle}, {profile.department}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Reports to: <span className="font-medium">{profile.managerName || 'N/A'}</span>
                    {profile.managerTitle && <span>, {profile.managerTitle}</span>}
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {profile.regionFlag} {profile.city} · {profile.tenure} yrs
                    </span>
                    <span className="flex items-center gap-1">
                      <Briefcase className="w-3 h-3" />
                      {profile.assessmentId}
                    </span>
                  </div>
                </div>
              </div>

              {/* Right: BRI Score Circle */}
              <div className="flex flex-col items-center gap-2">
                {hasAssessmentData ? (
                  <>
                    <div className={`w-24 h-24 rounded-full flex flex-col items-center justify-center border-4 ${
                      profile.briScore >= 85 ? 'border-[#1B5E20] bg-[#1B5E20]/10' :
                      profile.briScore >= 70 ? 'border-[#1565C0] bg-[#1565C0]/10' :
                      profile.briScore >= 55 ? 'border-[#F9A825] bg-[#F9A825]/10' : 'border-[#C62828] bg-[#C62828]/10'
                    }`}>
                      <span className={`text-3xl font-bold ${
                        profile.briScore >= 85 ? 'text-[#1B5E20]' :
                        profile.briScore >= 70 ? 'text-[#1565C0]' :
                        profile.briScore >= 55 ? 'text-[#F9A825]' : 'text-[#C62828]'
                      }`}>
                        {profile.briScore}
                      </span>
                      <span className="text-[10px] text-muted-foreground">BRI SCORE</span>
                    </div>
                    <SegmentBadge segment={profile.segment} size="lg" />
                    {profile.comparative?.percentile && (
                      <span className="text-xs text-muted-foreground">Top {100 - profile.comparative.percentile}% in Role</span>
                    )}
                  </>
                ) : (
                  <div className="w-24 h-24 rounded-full flex flex-col items-center justify-center border-4 border-slate-300 bg-slate-100">
                    <Lock className="w-6 h-6 text-slate-400" />
                    <span className="text-[10px] text-muted-foreground mt-1">PENDING</span>
                  </div>
                )}
              </div>
            </div>

            {/* Summary callout */}
            <div className="mt-4 p-4 bg-slate-50 rounded-lg border">
              <p className="text-sm text-slate-700">{profile.summary}</p>
            </div>

            {/* Quick badges */}
            <div className="flex flex-wrap gap-3 mt-4">
              <Badge variant="outline" className="gap-1">
                <Shield className="w-3 h-3" />
                RETENTION: {profile.badges?.retention || 'N/A'}
              </Badge>
              <Badge variant="outline" className="gap-1">
                <TrendingUp className="w-3 h-3" />
                ROI: {profile.badges?.roi}
              </Badge>
              <Badge variant="outline" className="gap-1">
                <DollarSign className="w-3 h-3" />
                RISK IF LOST: {profile.badges?.riskIfLost}
              </Badge>
              {profile.recommendedAction && (
                <ActionBadge action={profile.recommendedAction} size="lg" />
              )}
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Assessment Breakdown */}
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-[#1a3a3a]">
                  Assessment Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                {hasAssessmentData && profile.scores ? (
                  <div className="space-y-4">
                    {[
                      { key: 'selfAssessment', label: 'Self-Assessment', weight: '15%' },
                      { key: 'managerValidation', label: 'Manager Validation', weight: '15%' },
                      { key: 'technicalCompetency', label: 'Technical Competency', weight: '25%' },
                      { key: 'handsOnSimulation', label: 'Hands-On Simulation', weight: '25%' },
                      { key: 'collaboration', label: 'Collaboration', weight: '20%' }
                    ].map(dim => {
                      const score = profile.scores[dim.key];
                      const isAbove = score >= 80;
                      return (
                        <div key={dim.key} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-medium">{dim.label} ({dim.weight})</span>
                            <div className="flex items-center gap-2">
                              <span className={`font-bold ${isAbove ? 'text-emerald-600' : 'text-amber-600'}`}>
                                {score}
                              </span>
                              <Badge variant={isAbove ? "default" : "outline"} className={`text-[10px] ${isAbove ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                {isAbove ? 'ABOVE' : 'BELOW'}
                              </Badge>
                            </div>
                          </div>
                          <Progress value={score} className="h-2" />
                        </div>
                      );
                    })}
                    <div className="pt-3 border-t">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">Weighted Total</span>
                        <span className="text-2xl font-bold text-[#1a3a3a]">{profile.briScore}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Lock className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                    <p>Assessment data will appear once completed</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Decision Scenarios */}
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-[#1a3a3a]">
                  Decision Scenarios
                </CardTitle>
              </CardHeader>
              <CardContent>
                {hasAssessmentData ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Develop */}
                      <div className={`p-4 rounded-lg border-2 ${profile.scenarios?.develop?.recommended ? 'border-blue-500 bg-blue-50' : 'border-slate-200'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-blue-700">DEVELOP</h4>
                          {profile.scenarios?.develop?.recommended && (
                            <Badge className="bg-blue-600 text-white text-[10px]">✓ RECOMMENDED</Badge>
                          )}
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Investment:</span>
                            <span className="font-medium">{formatCurrency(profile.scenarios?.develop?.investment)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Outcome:</span>
                            <span className="font-medium text-xs">{profile.scenarios?.develop?.outcome}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Annual Value:</span>
                            <span className="font-medium text-emerald-600">+{formatCurrency(profile.scenarios?.develop?.annualValue)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">ROI:</span>
                            <span className="font-medium">{profile.scenarios?.develop?.roi}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Risk:</span>
                            <span className="font-medium text-emerald-600">{profile.scenarios?.develop?.risk}</span>
                          </div>
                        </div>
                      </div>

                      {/* Status Quo */}
                      <div className="p-4 rounded-lg border-2 border-slate-200">
                        <h4 className="font-semibold text-slate-700 mb-2">STATUS QUO</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Cost:</span>
                            <span className="font-medium">{formatCurrency(profile.scenarios?.statusQuo?.cost)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Outcome:</span>
                            <span className="font-medium text-xs">{profile.scenarios?.statusQuo?.outcome}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Value:</span>
                            <span className="font-medium text-red-600">{formatCurrency(profile.scenarios?.statusQuo?.value)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Risk:</span>
                            <span className="font-medium text-red-600">{profile.scenarios?.statusQuo?.risk}</span>
                          </div>
                          <Badge variant="outline" className="mt-2 text-amber-700 border-amber-300">
                            {profile.scenarios?.statusQuo?.exitProbability}% exit probability
                          </Badge>
                        </div>
                      </div>

                      {/* Transition */}
                      <div className={`p-4 rounded-lg border-2 ${profile.scenarios?.transition?.recommended ? 'border-red-500 bg-red-50' : 'border-slate-200'}`}>
                        <h4 className="font-semibold text-slate-700 mb-2">TRANSITION</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Cost:</span>
                            <span className="font-medium text-red-600">{formatCurrency(profile.scenarios?.transition?.cost)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Outcome:</span>
                            <span className="font-medium text-xs">{profile.scenarios?.transition?.outcome}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Value:</span>
                            <span className="font-medium text-red-600">{formatCurrency(profile.scenarios?.transition?.value)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Risk:</span>
                            <span className="font-medium text-amber-600">{profile.scenarios?.transition?.risk}</span>
                          </div>
                          {!profile.scenarios?.transition?.recommended && (
                            <Badge variant="outline" className="mt-2 text-slate-500">Not advised</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-4 text-center">
                      Develop outperforms alternatives by {formatCurrency((profile.scenarios?.develop?.annualValue || 0) * 3)} over 3 years
                    </p>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <Lock className="w-8 h-8 text-slate-400 mb-2" />
                    <p className="text-sm font-medium text-slate-600">Awaiting Assessment Data</p>
                    <p className="text-xs text-muted-foreground mt-1">Decision scenarios will be available once assessment is completed and reviewed</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Skills Heat Map */}
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-[#1a3a3a]">
                  Skills Assessment
                </CardTitle>
              </CardHeader>
              <CardContent>
                {hasAssessmentData && profile.skills?.length > 0 ? (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {profile.skills?.map((skill, i) => (
                        <div 
                          key={i}
                          className={`p-3 rounded-lg border ${
                            skill.gapStatus === 'Critical' ? 'bg-red-50 border-red-200' :
                            skill.gapStatus === 'Moderate' ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <span className="text-xs font-medium truncate flex-1">{skill.name}</span>
                            {skill.gapStatus === 'Critical' && skill.priority && (
                              <Badge className="bg-red-600 text-white text-[8px] ml-1">{skill.priority}</Badge>
                            )}
                          </div>
                          <StarRating rating={skill.proficiency} />
                        </div>
                      ))}
                    </div>
                    {profile.criticalGaps > 0 && (
                      <p className="text-xs text-red-600 mt-3 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {profile.criticalGaps} Critical Gaps blocking Builder status
                      </p>
                    )}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <Lock className="w-8 h-8 text-slate-400 mb-2" />
                    <p className="text-sm font-medium text-slate-600">Awaiting Assessment Data</p>
                    <p className="text-xs text-muted-foreground mt-1">Skills assessment will be available once assessment is completed and reviewed</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Investment Roadmap */}
            {profile.investmentRoadmap?.length > 0 && (
              <Card className="bg-white shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-[#1a3a3a]">
                    Investment Roadmap
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {profile.investmentRoadmap.map((item, i) => (
                      <div key={i} className="flex items-center gap-4 p-3 rounded-lg border bg-slate-50">
                        <div className={`w-2 h-2 rounded-full ${
                          item.status === 'Complete' ? 'bg-emerald-500' :
                          item.status === 'In Progress' ? 'bg-blue-500 animate-pulse' : 'bg-slate-300'
                        }`} />
                        <div className="flex-1">
                          <p className="font-medium text-sm">{item.quarter}: {item.activity}</p>
                        </div>
                        <span className="text-sm font-medium">{formatCurrency(item.cost)}</span>
                        <Badge variant={item.status === 'Complete' ? 'default' : 'outline'} className="text-[10px]">
                          {item.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-3 border-t">
                    <span className="font-semibold">Total Investment</span>
                    <span className="text-lg font-bold text-[#1a3a3a]">{formatCurrency(profile.totalInvestment)}</span>
                  </div>
                  <div className="mt-2 text-center">
                    <Badge className="bg-emerald-100 text-emerald-700">
                      <Target className="w-3 h-3 mr-1" />
                      BUILDER STATUS TARGET: Q4 2026
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Capability Radar */}
            {hasAssessmentData && profile.scores && (
              <Card className="bg-white shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-[#1a3a3a]">
                    Capability Radar
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <RadarChart scores={profile.scores} size={220} />
                  <div className="flex items-center justify-center gap-4 mt-2 text-xs">
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-[#1565C0]/20 border border-[#1565C0]" />
                      Current
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-0.5 border-t-2 border-dashed border-slate-400" style={{ width: 12 }} />
                      Threshold (80)
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Strategic Value */}
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-[#1a3a3a]">
                  Strategic Value
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {hasAssessmentData ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-slate-50 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground">Replacement Cost</p>
                        <p className="text-lg font-bold text-[#1a3a3a]">{formatCurrency(profile.strategicMetrics?.replacementCost)}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground">Development ROI</p>
                        <p className="text-lg font-bold text-emerald-600">{profile.strategicMetrics?.developmentROI}%</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground">Replacement Time</p>
                        <p className="text-lg font-bold text-[#1a3a3a]">{profile.strategicMetrics?.replacementTime} mo</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground">Flight Risk</p>
                        <p className={`text-lg font-bold ${
                          profile.strategicMetrics?.flightRiskLevel === 'High' ? 'text-red-600' :
                          profile.strategicMetrics?.flightRiskLevel === 'Medium' ? 'text-amber-600' : 'text-emerald-600'
                        }`}>
                          {profile.strategicMetrics?.flightRisk}%
                        </p>
                      </div>
                    </div>
                    <div className="p-3 bg-emerald-50 rounded-lg text-center border border-emerald-200">
                      <p className="text-xs text-emerald-700">
                        Every $1 invested saves ${((profile.strategicMetrics?.replacementCost || 0) / (profile.scenarios?.develop?.investment || 1)).toFixed(2)} in replacement costs
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-6 text-center">
                    <Lock className="w-6 h-6 text-slate-400 mb-2" />
                    <p className="text-sm font-medium text-slate-600">Awaiting Data</p>
                    <p className="text-xs text-muted-foreground mt-1">Strategic value metrics will appear once assessment is reviewed</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Risk & Opportunities */}
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-[#1a3a3a]">
                  Risk & Opportunities
                </CardTitle>
              </CardHeader>
              <CardContent>
                {hasAssessmentData ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-xs font-semibold text-red-700 mb-2">RISKS</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Flight Risk</span>
                          <Badge variant="outline" className={
                            profile.riskPanel?.flightRiskLevel === 'High' ? 'border-red-300 text-red-700' :
                            profile.riskPanel?.flightRiskLevel === 'Medium' ? 'border-amber-300 text-amber-700' : 'border-emerald-300 text-emerald-700'
                          }>
                            {profile.riskPanel?.flightRisk}%
                          </Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Competitors</span>
                          <span className="font-medium">{profile.riskPanel?.competitorInterest} approaches</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Inaction Risk</span>
                          <span className="font-medium text-amber-600">{profile.riskPanel?.inactionRisk}%</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-xs font-semibold text-emerald-700 mb-2">OPPORTUNITIES</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Builder Potential</span>
                          <span className="font-medium text-emerald-600">{profile.riskPanel?.builderPotential}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">ROI Potential</span>
                          <span className="font-medium">{profile.riskPanel?.roiPotential}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Team Impact</span>
                          <span className="font-medium">{profile.riskPanel?.teamImpact} mentees</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-6 text-center">
                    <Lock className="w-6 h-6 text-slate-400 mb-2" />
                    <p className="text-sm font-medium text-slate-600">Awaiting Data</p>
                    <p className="text-xs text-muted-foreground mt-1">Risk analysis will appear once assessment is reviewed</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Manager Endorsement */}
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-[#1a3a3a]">
                  Manager Endorsement
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center text-xs font-medium">
                    {profile.managerEndorsement?.managerName?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'MG'}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{profile.managerEndorsement?.managerName}</p>
                    <p className="text-xs text-muted-foreground">{profile.managerEndorsement?.managerTitle}</p>
                  </div>
                </div>
                <blockquote className="text-sm italic text-slate-600 border-l-2 border-slate-300 pl-3 mb-3">
                  &ldquo;{profile.managerEndorsement?.quote}&rdquo;
                </blockquote>
                <div className="flex flex-wrap gap-2 mb-3">
                  <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">
                    {profile.managerEndorsement?.endorsementLevel}
                  </Badge>
                </div>
                <div className="space-y-1 text-xs">
                  {profile.managerEndorsement?.validations?.managerAligned && (
                    <p className="flex items-center gap-1 text-emerald-600">
                      <CheckCircle2 className="w-3 h-3" /> Manager aligned
                    </p>
                  )}
                  {profile.managerEndorsement?.validations?.hrValidated && (
                    <p className="flex items-center gap-1 text-emerald-600">
                      <CheckCircle2 className="w-3 h-3" /> HR validated
                    </p>
                  )}
                  {profile.managerEndorsement?.validations?.investmentApproved && (
                    <p className="flex items-center gap-1 text-emerald-600">
                      <CheckCircle2 className="w-3 h-3" /> Investment approved
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Final Recommendation Bar */}
        <Card className="bg-[#1a3a3a] text-white mt-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <span className="text-sm font-medium">FINAL RECOMMENDATION</span>
              <div className="flex items-center gap-4">
                <ActionBadge action={profile.finalRecommendation?.action} size="lg" />
                <div className="text-sm">
                  <span className="text-white/70">Investment: </span>
                  <span className="font-medium">{formatCurrency(profile.finalRecommendation?.investment)}</span>
                </div>
                <div className="text-sm">
                  <span className="text-white/70">Timeline: </span>
                  <span className="font-medium">{profile.finalRecommendation?.timeline}</span>
                </div>
                <div className="text-sm">
                  <span className="text-white/70">Target: </span>
                  <span className="font-medium">{profile.finalRecommendation?.targetDate}</span>
                </div>
              </div>
              <Badge className={profile.finalRecommendation?.approvalStatus === 'APPROVED' ? 'bg-emerald-500' : 'bg-amber-500'}>
                {profile.finalRecommendation?.approvalStatus}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => navigateToEmployee(-1)}
            disabled={currentIndex <= 0}
            className="gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous Employee
          </Button>
          <span className="text-sm text-muted-foreground">
            {currentIndex + 1} of {employeeList.length} employees
          </span>
          <Button
            variant="outline"
            onClick={() => navigateToEmployee(1)}
            disabled={currentIndex >= employeeList.length - 1}
            className="gap-2"
          >
            Next Employee
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </main>
    </div>
  );
}
